/**
 * This file contains code shared by Mimir Desktop and Mimir Adobe Panels in Premiere, AE, Photoshop.
 */

import { showToast, TOAST } from '@mimir/web/eventBus';
import { SystemPath } from '@mimir/web/premiere/cep';
import { getSystemPath } from '@mimir/web/premiere/fileHelpers';
import { getGlobalStore } from '@mimir/web/store';
import { getItemAncestry, getFolderAncestry } from '@mimir/web/api/globalClientFolder';
import { logStatistic } from '@mimir/web/util/api';
import { getSanitizedDownloadProgress } from '@mimir/web/util/downloadProgress';
import type { LocalPathInfo } from '@mimir/client-common/fileSync/premiereTypes';
import type { OngoingDownload } from '@mimir/client-common/model/ongoingDownload';
import { getId } from '@mimir/client-common/model/ongoingDownload';
import { getItemLocalDirsFromPaths, getItemLocalDirs } from '@mimir/client-common/util/folder';
import type { FolderPathSegmentDto } from '@mimir/common/dto/folderModels';
import type { SearchResultHit, Item, LoadedItem } from '@mimir/common/dto/searchResultHit';
import { ItemType } from '@mimir/common/dto/itemTypes';
import type { ItemArtefact } from '@mimir/common/dto/transferRequestDtos';
import { getEditProxyExtension, isDownloadable, isPlaceholderItem, isSearchHitProxyOnlyItem, wasHighresDeleted } from '@mimir/common/util/item';
import { hasMediaSegments } from '@mimir/common/item/media';
import { invariant } from '@mimir/common/util/invariant';
import { isDirAvailableLocally, recursiveMkdirSync, linkFileToDestinations, encodeForFs, testDirWritability } from '@mimir/common/util/fs';
import isEmpty from 'lodash/isEmpty';
import type { CopyParams, LocalPathCopyResult } from '@mimir/common/upload/fileCopyUpload';
import type { StorageProfile } from '@mimir/web/desktop/storageSettings';
import { isVirtualClip } from '@mimir/common/dto/virtualClipUtil';
import { getBasenameWithoutExtension, getExtname } from '@mimir/common/platformAbstraction/pathComposition';
import { transfer } from '../globals/transfer';
import { type AdobePanelContext, type ElectronContext, isInAdobeApp, type ApplicationContext, isInElectron } from '@mimir/common/context/applicationContext';
import type { Logger } from '@mimir/common/types/logger';
import { ORPHAN_LINKS_DIR } from '@mimir/common/util/orphans';
import { getElectronIpcRenderer } from './renderer';
import { getFileName } from '@mimir/client-common/fileSync/defaultLocalStateOps';
import { makeUiDownloadRequest, type UiDownloadConfig } from '../store/uiDownload';
import { planDownload } from '@mimir/client-common/fileSync/downloadPlanning';
import { getLocalMediaState } from '../globals/localMediaState';
import type { DownloadIdentity } from '@mimir/client-common/fileSync/downloadIdentity';

export const getElectronAppObj = (applicationContext: ElectronContext) => {
  const req = applicationContext.mainRequire;
  if (!req) {
    return;
  }
  return req('electron').app;
};

export const getUserDataPath = (applicationContext: AdobePanelContext | ElectronContext) => {
  let result;
  if (isInElectron(applicationContext)) {
    result = getElectronAppObj(applicationContext).getPath('appData');
  } else {
    // premiere
    result = getSystemPath(applicationContext, SystemPath.USER_DATA);
  }

  return result;
};

export const getElectronShell = (applicationContext: ElectronContext) => {
  const req = applicationContext.mainRequire;
  if (!req) {
    return;
  }
  return req('electron').shell;
};

// orphan items do not belong to any folders. their links are created in a separate folder so as not to pollute the root
export const getOrphanLinkPath = (downloadPath: string, sep: string, itemId: string, fileName: string) => {
  return `${downloadPath}${sep}${ORPHAN_LINKS_DIR}${sep}${itemId}${sep}${fileName}`;
};


export const getFolderPath = async (applicationContext: AdobePanelContext | ElectronContext, folderId: string, excludeRootFolder = false) => {
  const folderAncestry = await getFolderAncestry([folderId], 'folder');
  if (!folderAncestry || !folderAncestry[folderId] || !folderAncestry[folderId].paths) {
    return '';
  }

  const pathArrOfArrs = Object.values(folderAncestry[folderId].paths)[0];
  if (!pathArrOfArrs || pathArrOfArrs.length === 0) {
    return '';
  }

  const pathArrs = pathArrOfArrs[0];
  if (!pathArrs || pathArrs.length === 0) {
    return '';
  }

  const sep = applicationContext.pathSep;
  const folders = pathArrs.map(p => encodeForFs(p.name));
  if (excludeRootFolder) folders.shift();
  return folders.join(sep);
};

export const getFolderLocalPath = async (
  config: {
    isProxyMode: boolean,
    downloadPath: string,
  },
  applicationContext: AdobePanelContext | ElectronContext,
  folderId: string,
) => {
  const sep = applicationContext.pathSep;
  if (!config.downloadPath) {
    return;
  }

  const downloadPath = config.isProxyMode ? `${config.downloadPath}${sep}proxy` : config.downloadPath;

  const mimirPath = await getFolderPath(applicationContext, folderId);
  const result = `${downloadPath}${sep}${mimirPath}`;

  return result;
};

export const getLinkPath = (
  applicationContext: AdobePanelContext | ElectronContext,
  hit: SearchResultHit,
  flattenItemAncestors: FolderPathSegmentDto[][] | null,
  artefact: ItemArtefact,
) => {
  const sep = applicationContext.pathSep;
  let downloadPath = getGlobalStore().getters.downloadPath as string;

  let fileName = getFileName(hit);

  if (!hit || !fileName || !downloadPath) {
    return;
  }

  if (artefact === 'proxy') {
    downloadPath = `${downloadPath}${sep}proxy`;
  } else if (artefact === 'aaf') {
    downloadPath = `${downloadPath}${sep}aaf`;
  }

  /*
    fileName only contains a basename, not a full
    path. It therefore doesn't matter which path separator we use when
    stripping the extension.
  */
  const origSep = '/';
  const shortName = getBasenameWithoutExtension(fileName, origSep);
  if (artefact === 'highres' && fileName === shortName) {
    // There is no extension, which suggests that the item was a live recording
    if (hit.mediaType === 'video/mp2t' || hit.mediaType === 'audio/mp2t') {
      fileName = `${shortName}.ts`;
    }
  } else if (artefact === 'proxy' && hit.itemType === ItemType.VIDEO) {
    fileName = `${shortName}${getEditProxyExtension(hit)}`;
  } else if (artefact === 'aaf') {
    fileName = `${shortName}.aaf`;
  }

  const itemPaths = getItemLocalDirsFromPaths(hit, downloadPath, sep, flattenItemAncestors || [[]]);
  if (itemPaths.length > 0) {
    return `${itemPaths[0]}${sep}${fileName}`;
  } else {
    return getOrphanLinkPath(downloadPath, sep, hit.id, fileName);
  }
};

export async function makeDir(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    logger: Logger,
  },
  dirPath: string,
) {
  if (await isDirAvailableLocally(services.applicationContext.nodeApi, services.logger, dirPath)) {
    return;
  }

  // On windows, makedir does not work for multi level dirs without having backslashes.
  if (services.applicationContext.isWindows) {
    dirPath = dirPath.replace(/\//g, '\\');
  }

  services.logger.info(`recursiveMkdirSync: ${dirPath}`);
  recursiveMkdirSync(services.applicationContext.nodeApi, dirPath);
}

export const copyFileInMainProcess = async (applicationContext: ElectronContext, params: CopyParams): Promise<LocalPathCopyResult> => {
  return applicationContext.mainRequire('./utils').copyFileInMainProcess(params);
};

export const cancelCopyFileInMainProcess = (applicationContext: ElectronContext, executionId: string): Promise<LocalPathCopyResult> => {
  return applicationContext.mainRequire('./utils').cancelCopyFileInMainProcess(executionId);
};

export const getSelectedDirectory = async (applicationContext: ApplicationContext): Promise<string | null> => {

  if (isInElectron(applicationContext)) {
    const electronReq = applicationContext.mainRequire;
    invariant(electronReq, 'electronReq should be defined in Electron context');
    const result = await electronReq('electron').dialog.showOpenDialog({
      properties: ['openDirectory']
    });

    return result && result.filePaths && result.filePaths.length && result.filePaths[0] || '';
  } else if (isInAdobeApp(applicationContext)) {
    // When using the Folder.selectDialog() API of ExtendScript context, the dialog can appear behind
    // the host application in Windows and it shows the simplistic folder selection dialog of Windows.
    // To fix this issue, we are using window.cep.fs.showOpenDialogEx(). This seems to work better on
    // Windows.
    const result = window.cep.fs.showOpenDialogEx(false, true, 'Choose new directory');
    if (result.data.length) {
      return applicationContext.nodeApi.path.normalize(result.data[0]);
    } else if (result.err) {
      throw new Error(`getSelectedDirectory: ${result.err}`);
    }
    return '';
  }

  return null;
};

export async function maybeSelectLocalDirectory(applicationContext: ApplicationContext): Promise<string | null> {
  const result = await getSelectedDirectory(applicationContext);
  if (result == '') {
    return null;
  }

  return result;
}

export function canSelectLocalDirectory(applicationContext: ApplicationContext): boolean {
  return isInElectron(applicationContext) || isInAdobeApp(applicationContext);
}

export async function linkItemToDestinations(
  services: {
    applicationContext: ElectronContext,
    logger: Logger,
  },
  config: {
    isProxyMode: boolean,
    downloadPath: string,
  },
  sourceFilename: string,
  item: Pick<Item, 'id' | 'originalFileName' | 'itemType' | 'aaf' | 'folderParents' | 'editProxyFormat' | 'itemState'>,
  downloadIdentity: DownloadIdentity,
): Promise<void> {
  let linksBaseDir = config.downloadPath;
  if (!linksBaseDir) {
    return;
  }

  let fileName = getFileName(item);
  if (!fileName) {
    return;
  }
  const sep = services.applicationContext.pathSep;

  /*
    fileName only contains a basename, not a full
    path. It therefore doesn't matter which path separator we use when
    retrieving or stripping the extension.
  */
  const origSep = '/';
  const originalFileExt = getExtname(fileName!, origSep);
  let linkFileExtension = originalFileExt;
  if (item.aaf) {
    linksBaseDir = `${linksBaseDir}${sep}aaf`;
    linkFileExtension = '.aaf';
  /**
   *  NOTE: It is very important that we are making this decision based on the
   *  state of the UI and not based on the artifact that we are referencing.
   *  The reason for this is that for instance when we download images in proxy
   *  mode we still end up downloading the "highres", but we still need to
   *  replicate Mimir folder structure on disk in the "proxy" subfolder so that
   *  the drag icons work correctly.
   *
   *  You could of course solve this by instead pointing the drag path to the
   *  highres folder for images, but then that would not work when a folder
   *  contains a mix of images and videos for instance.
   */
  } else if (config.isProxyMode) {
    linksBaseDir = `${linksBaseDir}${sep}proxy`;
    if (item.itemType === ItemType.VIDEO) {
      linkFileExtension = getEditProxyExtension(item);
    }
  }

  const localDirs = await getItemLocalDirs(item, linksBaseDir, sep,
    (itemId) => getItemAncestry(itemId, undefined));

  if ((item.aaf || (downloadIdentity.artefact === 'proxy')) && item.itemType === ItemType.VIDEO) {
    const shortName = getBasenameWithoutExtension(fileName, origSep);
    fileName = `${shortName}${linkFileExtension}`;
  }
  if (!isEmpty(localDirs)) {
    const linkPaths = localDirs.map(dir => {
      return {
        path: `${dir}${sep}${fileName}`,
      };
    });
    await linkFileToDestinations(services.applicationContext.nodeApi, services.logger, sourceFilename, linkPaths, true);
  } else {
    const linkPath = {
      path: getOrphanLinkPath(linksBaseDir, sep, item.id, fileName),
    };
    await linkFileToDestinations(services.applicationContext.nodeApi, services.logger, sourceFilename, [linkPath], true);
  }
}

export function getLocalPathInfo(itemId: string) {
  return (getGlobalStore().getters.localPathInfo[itemId] || {}) as LocalPathInfo;
}

export function prepareLocalPathInfo(
  isProxyMode: boolean,
  applicationContext: AdobePanelContext | ElectronContext,
  item: SearchResultHit,
  flattenItemAncestors: FolderPathSegmentDto[][] | null,
) {
  // clone the path info before updating its props, since it comes from vuex store
  // and the updates made here may get overwritten to cause reactivity issues.
  const localPathInfo = JSON.parse(JSON.stringify(getLocalPathInfo(item.id)));
  if (item.aaf) {
    localPathInfo.aafLinkPath = getLinkPath(applicationContext, item, flattenItemAncestors, 'aaf');
  } else if (isProxyMode) {
    localPathInfo.proxyLinkPath = getLinkPath(applicationContext, item, flattenItemAncestors, 'proxy');
  } else {
    localPathInfo.highresLinkPath = getLinkPath(applicationContext, item, flattenItemAncestors, 'highres');
  }

  // cache ancestor paths to be able to refresh quickly later
  localPathInfo.ancestorPaths = flattenItemAncestors;
  return localPathInfo;
}

// recomputes localPathInfo, but with cached ancestry info, useful on proxy<->highres mode switch
export function quickRefreshLocalPathInfo(isProxyMode: boolean, applicationContext: AdobePanelContext | ElectronContext, item: Item) {
  const cachedLocalPathInfo = getLocalPathInfo(item.id);
  const newLocalPathInfo = prepareLocalPathInfo(isProxyMode, applicationContext, item, cachedLocalPathInfo?.ancestorPaths || null);
  void getGlobalStore().dispatch('setLocalPathInfo', { id: item.id, localPathInfo: newLocalPathInfo });
}

export async function validateWritabilityIfStandaloneProfile(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    logger: Logger,
  },
  profile: StorageProfile,
) {
  if (!profile.isStandaloneMode) {
    return;
  }
  return validateWritability(services, profile.downloadPath);
}

export async function validateWritability(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    logger: Logger,
  },
  selectedDirectory: string, suppressErrorPopup = false,
) {
  const result = await testDirWritability(services.applicationContext.nodeApi, services.logger, selectedDirectory);
  if (!result.isWritable) {
    if (!suppressErrorPopup) showToast(`Unable to write to ${selectedDirectory}. Please check permissions.`, TOAST.ERROR);

    return false;
  }

  if (isInElectron(services.applicationContext)) {
    if (result.isLinkSupported) {
      getGlobalStore().commit('folderDragEnabled', true);
    } else {
      getGlobalStore().commit('folderDragEnabled', false);
      if (!suppressErrorPopup) showToast(`Mimir Desktop will have limited functionality since link creation not supported in ${selectedDirectory}. `, TOAST.WARNING);
    }

    return true;
  }

  return true;
}

export function getOdItemId(item: Item | LoadedItem) {
  // A separate AAF file is generated for virtual clips of AAF items
  const odItemId = isVirtualClip(item.virtualClipInfo) && !item.aaf
    ? item.virtualClipInfo.masterClipId
    : item.id;

  return odItemId;
}

export function getOngoingDownload(item: Item | LoadedItem, artefact: ItemArtefact) {
  const odId = getId({ artefact, itemId: getOdItemId(item), artifactReference: { type: 'ImplicitReference' } });
  const ongoingDownload: OngoingDownload = transfer.ongoingDownloads[odId];
  if (ongoingDownload && !ongoingDownload.initiatedFromStorageDownloadMenu) {
    return ongoingDownload;
  }
  return null;
}

export function getOngoingProxyDownload(item: Item | LoadedItem) {
  return getOngoingDownload(item, 'proxy');
}

export function getOngoingHighresDownload(item: Item | LoadedItem) {
  return getOngoingDownload(item, 'highres');
}

export function getDownloadProgress(item: Item | LoadedItem) {
  const odh = getOngoingHighresDownload(item);
  if (odh) {
    return getSanitizedDownloadProgress(odh);
  } else {
    return getSanitizedDownloadProgress(getOngoingProxyDownload(item));
  }
}

export function getTimedMdfIsLoading() {
  return getGlobalStore().getters.isLoadingItem || getGlobalStore().getters['timedMdf/collectionIsLoading'];
}

export function isProxyWorkflowEnabled() {
  return getGlobalStore().getters.isProxyWorkflowEnabled;
}

export function getPathToDrag(item: Item | LoadedItem) {
  let pathToDrag: string | undefined;
  const localPathInfo = getLocalPathInfo(item.id);
  if (item.aaf) {
    pathToDrag = localPathInfo.aafLinkPath;
  } else {
    pathToDrag = isProxyWorkflowEnabled() ? localPathInfo.proxyLinkPath : localPathInfo.highresLinkPath;
  }

  return pathToDrag ?? null;
}

export function getImportButtonDisabledTooltip(
  applicationContext: ApplicationContext,
  item: Item | LoadedItem,
  isGeneratingVirtualClipAaf: boolean,
  uiDownloadConfig: UiDownloadConfig,
): string | null {
  const req = makeUiDownloadRequest(uiDownloadConfig, applicationContext, item, undefined);
  const plan = planDownload(applicationContext, req);
  if (plan.blocker !== null) {
    return plan.blocker;
  }

  if (wasHighresDeleted(item)) {
    return 'Item\'s highres has been deleted';
  }

  if (hasMediaSegments(item) && isProxyWorkflowEnabled()) {
    if (!getDownloadProgress(item).initiated) {
      // This is a fix for making sure live items can always be imported.
      return null;
    }

    return 'An import is already in progress for this item';
  }
  if (isEmpty(getLocalMediaState(plan.dlId))) {
    return 'Looking for available media';
  }
  if (!isDownloadable(item)) {
    return 'Item is not downloadable';
  }
  if (isPlaceholderItem(item)) {
    return 'Item has no media yet.';
  }
  if (getTimedMdfIsLoading()) {
    return 'Loading ..';
  }
  if (isGeneratingVirtualClipAaf) {
    return 'Currently downloading';
  }
  if (isSearchHitProxyOnlyItem(item)) {
    return 'Can\'t import item as it only has proxy';
  }

  if (isInElectron(applicationContext) && item.itemType !== ItemType.CLIP_LIST && item.itemType !== ItemType.TIMELINE && !getPathToDrag(item)) {
    return 'Setting local path';
  }
  return null;
}

/**
 * When dragging a media from Mimir Desktop to drop it outside into other applications, we need to
 * send message to the main process and start the drag operation in that process instead of continuing
 * in the renderer process. The originating drag event in the renderer process must be stopped from
 * propagating futher and default handling must also be prevented (@dragstart.stop.prevent). Otherwise,
 * when the drop completes, Mimir Desktop crashes on Windows, probably as a result of the drag action
 * still being owned by the renderer as well as the main processes.
 *
 * On the other hand, Electron API is still limited and does not come back with any event when the drop
 * completes, not even to the main process.
 *
 * This makes is hard to distinguish between a file drag operation originated from Mimir Desktop itself
 * and one originated outside of it (e.g., to upload a video file) - which in turn makes it hard to know
 * whether to show the dropzone or not. We can turn on a flag to mark the start of the drag from Mimir
 * Desktop. But it is difficult to know when to turn this flag off.
 *
 * Here, we are implementing a hack by setting a 5 second timer, after which the flag will be turned off
 * automatically. This means that a Desktop drag operation will not show a dropzone for 5 seconds, and
 * hence drop-to-upload functionality will also be unavailable for that time.
 */
export function startDesktopDrag(applicationContext: ElectronContext, filePaths: string[], iconDataUrl: string | undefined) {
  if (getGlobalStore().getters.desktopDragTimerId) {
    clearTimeout(getGlobalStore().getters.desktopDragTimerId);
  }

  const newDesktopDragTimerId = window.setTimeout(() => {
    getGlobalStore().commit('desktopDragTimerId', null);
  }, 5000);

  getGlobalStore().commit('desktopDragTimerId', newDesktopDragTimerId);

  console.log(`startDesktopDrag(${JSON.stringify({ filePaths, iconDataUrl })})`);

  getElectronIpcRenderer(applicationContext)?.send('onDragStart', filePaths[0], iconDataUrl);
}

export const getMimirDesktopLogLocation = (applicationContext: ElectronContext) => {
  return getElectronAppObj(applicationContext)?.getPath('logs');
};

export const openPath = (applicationContext: ElectronContext, path: string) => {
  const shell = getElectronShell(applicationContext);
  invariant(shell, 'Electron shell not found');
  shell.openPath(path);
};

export const getMimirDesktopVersion = (applicationContext: ElectronContext) => {
  return getElectronAppObj(applicationContext)?.getVersion();
};

export async function logMimirDesktopVersion(applicationContext: ElectronContext) {
  try {
    const version = getMimirDesktopVersion(applicationContext);
    console.log(`Running Mimir Desktop: ${version}`);
    if (version) {
      await logStatistic({countType: 'mimir_desktop_version', value: version});
    } else {
      console.warn('Failed getting Mimir Desktop version');
    }
  } catch (e) {
    console.warn('Failed logging Mimir Desktop version', e);
  }
}
